<template>
  <div>
    <div class="input-group mb-3">
      <span class="input-group-text bg-transparent border-end-0">
        <font-awesome-icon icon="magnifying-glass" />
      </span>
      <input
        v-model="searchTerm"
        type="text"
        class="form-control border-start-0"
        placeholder="Find business registration"
        aria-label="Find business registration"
        @keyup.enter="newSearch"
      />
      <button type="button" class="btn btn-primary" :disabled="loading" @click="newSearch">
        {{ loading ? 'Searching...' : 'Search' }}
      </button>
      <button
        type="button"
        class="btn btn-outline-secondary"
        :disabled="loading || (!searchTerm && !resultsModel)"
        @click="reset"
      >
        Reset
      </button>
    </div>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <div v-if="loading && !resultsModel" class="alert alert-info" role="status">
      Loading business registrations...
    </div>

    <div
      v-else-if="!errorMessage && resultsModel && results.length === 0"
      class="alert alert-warning"
      role="status"
    >
      There are no matching business registrations for this search.
    </div>

    <div v-else-if="results.length > 0" class="table-responsive">
      <table class="table table-hover align-middle mb-0">
        <thead>
          <tr>
            <th scope="col">BRN</th>
            <th scope="col">Business Name</th>
            <th scope="col">Companies House Number</th>
            <th scope="col">Business Type</th>
            <th scope="col">Status</th>
            <th scope="col"><span class="visually-hidden">Select registration</span></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="businessRegistration in results"
            :key="businessRegistration.businessRegistrationNumber"
          >
            <td>{{ displayValue(businessRegistration.businessRegistrationNumber) }}</td>
            <td>{{ businessName(businessRegistration) }}</td>
            <td>{{ displayValue(businessRegistration.companiesHouseNumber) }}</td>
            <td>{{ displayValue(businessRegistration.businessType) }}</td>
            <td>
              <span class="badge rounded-pill" :class="statusClass(businessRegistration.status)">
                {{ displayValue(businessRegistration.status) }}
              </span>
            </td>
            <td class="text-end">
              <button
                type="button"
                class="btn"
                :class="isSelected(businessRegistration) ? 'btn-primary' : 'btn-outline-primary'"
                @click="select(businessRegistration)"
              >
                {{ isSelected(businessRegistration) ? 'Selected' : 'Select' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <nav
      v-if="resultsModel && resultsModel.totalPages > 1"
      class="mt-3"
      aria-label="Business registrations"
    >
      <ul class="pagination mb-0">
        <li class="page-item" :class="{ disabled: !canGoBack }">
          <button type="button" class="page-link" :disabled="!canGoBack" @click="selectPage(0)">
            First
          </button>
        </li>
        <li class="page-item" :class="{ disabled: !canGoBack }">
          <button
            type="button"
            class="page-link"
            :disabled="!canGoBack"
            @click="selectPage(pageNumber - 1)"
          >
            Previous
          </button>
        </li>
        <li class="page-item active" aria-current="page">
          <span class="page-link">{{ pageNumber + 1 }} of {{ resultsModel.totalPages }}</span>
        </li>
        <li class="page-item" :class="{ disabled: !canGoForward }">
          <button
            type="button"
            class="page-link"
            :disabled="!canGoForward"
            @click="selectPage(pageNumber + 1)"
          >
            Next
          </button>
        </li>
        <li class="page-item" :class="{ disabled: !canGoForward }">
          <button
            type="button"
            class="page-link"
            :disabled="!canGoForward"
            @click="selectPage(resultsModel.totalPages - 1)"
          >
            Last
          </button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import BusinessRegistrationSearchService from '../services/business-registration-search-service';

export default {
  name: 'BusinessRegistrationSelector',
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    pageSize: {
      type: Number,
      default: 10,
    },
  },
  emits: ['select', 'update:modelValue'],
  data() {
    return {
      errorMessage: '',
      loading: false,
      pageNumber: 0,
      resultsModel: null,
      searchTerm: '',
    };
  },
  computed: {
    canGoBack() {
      return !this.loading && this.pageNumber > 0;
    },
    canGoForward() {
      return (
        !this.loading && this.resultsModel && this.pageNumber < this.resultsModel.totalPages - 1
      );
    },
    results() {
      return this.resultsModel?.results || [];
    },
  },
  mounted() {
    this.search();
  },
  methods: {
    businessName(businessRegistration) {
      const registeredName = businessRegistration.registeredCompanyName;
      const tradingName = businessRegistration.tradingCompanyName;
      if (!tradingName) return this.displayValue(registeredName);
      return registeredName ? `${registeredName} t/a ${tradingName}` : tradingName;
    },
    displayValue(value) {
      return value || '-';
    },
    isSelected(businessRegistration) {
      return businessRegistration.businessRegistrationNumber === this.modelValue;
    },
    newSearch() {
      this.pageNumber = 0;
      this.search();
    },
    reset() {
      this.searchTerm = '';
      this.pageNumber = 0;
      this.search();
    },
    async search() {
      if (this.loading) return;

      this.errorMessage = '';
      this.loading = true;
      try {
        this.resultsModel = await BusinessRegistrationSearchService.search({
          fieldNames: ['businessRegistrationNumber', 'registeredCompanyName'],
          operator: 1,
          pageParameters: {
            number: this.pageNumber,
            size: this.pageSize,
          },
          searchTerm: this.searchTerm?.trim() || null,
          sort: {
            direction: 'ascending',
            item: 'registeredCompanyName',
          },
        });
      } catch (error) {
        this.errorMessage = error.response?.data?.message || error.message || error.toString();
      } finally {
        this.loading = false;
      }
    },
    select(businessRegistration) {
      this.$emit('update:modelValue', businessRegistration.businessRegistrationNumber);
      this.$emit('select', businessRegistration);
    },
    selectPage(pageNumber) {
      if (
        this.loading ||
        pageNumber < 0 ||
        !this.resultsModel ||
        pageNumber >= this.resultsModel.totalPages
      ) {
        return;
      }

      this.pageNumber = pageNumber;
      this.search();
    },
    statusClass(status) {
      switch (status) {
        case 'Active':
          return 'bg-success';
        case 'Suspended':
          return 'bg-warning text-dark';
        case 'Deregistered':
          return 'bg-danger';
        default:
          return 'bg-primary text-light';
      }
    },
  },
};
</script>
